/**
 * The assembled Sixty-six GameModule (docs/rules/sixty-six.md).
 *
 * Sixty-six is the game the engine's other four are not: two players, six
 * cards each, and a talon both hands are refilled from after every trick
 * until someone closes it or it runs out. The core's hooks carry that:
 *
 *  - the deal turns up the trump card as the "kitty" (face up for both) and
 *    leaves the other eleven as the stock — the talon;
 *  - there is no auction: the turned card is the contract, settled the moment
 *    the deal is done;
 *  - `afterTrick` draws from the talon, the trick's winner first, the loser
 *    taking the turned card with the last draw;
 *  - the leader's three choices before leading — exchange the trump Nine,
 *    close the talon, declare a marriage — are `turnDecisions`;
 *  - `handOver` ends the hand the moment a count reaches 66, which is going
 *    out. The engine goes out for a player as soon as they can (it never pays
 *    to wait: the other count can only grow), so a wrong call cannot happen.
 */
import { SUIT_LABEL, SUITS, suitOf } from '../../core/cards.js';
import { individualTable } from '../../core/seats.js';
import { DEFAULT_SIXTY_SIX_CONFIG, SIXTY_SIX_MARRIAGE, SIXTY_SIX_MAX_TRICKS, SIXTY_SIX_OUT, SIXTY_SIX_TRUMP_MARRIAGE, validateSixtySixConfig, } from './config.js';
import { sixtySixCountOf, sixtySixLeadMarriage, sixtySixOpponent, sixtySixCurrentTrick, sixtySixDealPlan, sixtySixDeck, sixtySixLegalPlays, sixtySixRanking, sixtySixVisibility, sixtySixShown, sixtySixTalon, } from './rules.js';
import { sixtySixContractLabel, sixtySixMatchEnd, sixtySixNextDealer, sixtySixScoreHand, } from './scoring.js';
export const SIXTY_SIX_TABLE = individualTable(2);
const INIT_EXTRA = {
    trump: null, marriages: [], exchange: null, closed: null, strictFrom: null, shown: [],
};
/** No bidding: the card turned up at the deal names trumps (rules §2). */
export const sixtySixAuction = {
    init({ dealer, faceUp }) {
        return { dealer, trumpCard: faceUp?.[0] ?? null };
    },
    legalCalls: () => [],
    apply: (s) => s,
    status(s) {
        const suit = s.trumpCard ? suitOf(s.trumpCard) : null;
        if (!s.trumpCard || !suit)
            return { kind: 'passedOut' };
        return { kind: 'contract', contract: { trump: suit, trumpCard: s.trumpCard, label: SUIT_LABEL[suit] } };
    },
    callKey: () => 'none',
    describe: () => ({ label: '', explain: '' }),
};
const NINE = 9;
const QUEEN = 12;
const KING = 13;
/** The leader's choices before leading (rules §4.3, §5, §6), in the order a
 *  player would think of them: exchange first, so a trump card taken can make
 *  a marriage; marriages; closing last. */
export function sixtySixTurnDecisions(hand, seat) {
    const trump = hand.extra.trump;
    const trick = sixtySixCurrentTrick(hand);
    if (!trump || hand.phase !== 'play' || trick.plays.length > 0 || trick.leader !== seat)
        return [];
    const holding = hand.secret.holdings[seat] ?? [];
    const count = sixtySixCountOf(hand);
    const open = hand.extra.closed === null;
    const out = [];
    // §4.3: on lead, with a trick taken, while the talon is open and holds more
    // than the turned card and one more.
    const nine = `${NINE}${trump}`;
    const turned = hand.secret.kitty[0];
    if (open && count.tricks[seat] > 0 && sixtySixTalon(hand) > 2 && holding.includes(nine) && turned && turned !== nine) {
        out.push({ key: 'exchange', decision: { type: 'exchange' }, label: `Exchange 9${SUIT_LABEL[trump]}` });
    }
    // §5: one marriage per lead; any time you are on lead.
    if (sixtySixLeadMarriage(hand) === null) {
        for (const suit of SUITS) {
            if (holding.includes(`${KING}${suit}`) && holding.includes(`${QUEEN}${suit}`)) {
                const points = suit === trump ? SIXTY_SIX_TRUMP_MARRIAGE : SIXTY_SIX_MARRIAGE;
                out.push({ key: `marriage:${suit}`, decision: { type: 'marriage', suit }, label: `Declare ${points} ${SUIT_LABEL[suit]}` });
            }
        }
    }
    // §6.1: not before the first trick, and not with only two cards left.
    if (open && trick.index > 0 && sixtySixTalon(hand) > 2) {
        out.push({ key: 'close', decision: { type: 'close' }, label: 'Close talon' });
    }
    return out;
}
export function sixtySixDecisionKey(d) {
    if (d.type === 'marriage') {
        if (!SUITS.includes(d.suit))
            throw new Error('unknown suit');
        return `marriage:${d.suit}`;
    }
    if (d.type === 'exchange' || d.type === 'close')
        return d.type;
    throw new Error('unknown decision');
}
function applySixtySixDecision(hand, seat, d) {
    const trump = hand.extra.trump;
    if (!trump)
        return { ok: false, error: 'No trump yet' };
    const trick = sixtySixCurrentTrick(hand);
    const extra = hand.extra;
    if (d.type === 'exchange') {
        const nine = `${NINE}${trump}`;
        const turned = hand.secret.kitty[0];
        if (!turned)
            return { ok: false, error: 'No trump card to take' };
        const holdings = hand.secret.holdings.map((h, s) => (s === seat ? [...h.filter((c) => c !== nine), turned] : h));
        return {
            ok: true,
            hand: {
                ...hand,
                secret: { ...hand.secret, holdings, kitty: [nine] },
                extra: { ...extra, exchange: { seat, gave: nine, took: turned }, shown: [...extra.shown, { seat, card: turned }] },
            },
        };
    }
    if (d.type === 'close') {
        const opponentHadTrick = sixtySixCountOf(hand).tricks[sixtySixOpponent(seat)] > 0;
        return {
            ok: true,
            hand: { ...hand, extra: { ...extra, closed: { seat, trick: trick.index, opponentHadTrick }, strictFrom: extra.strictFrom ?? trick.index } },
        };
    }
    const points = d.suit === trump ? SIXTY_SIX_TRUMP_MARRIAGE : SIXTY_SIX_MARRIAGE;
    const pair = [`${KING}${d.suit}`, `${QUEEN}${d.suit}`];
    return {
        ok: true,
        hand: {
            ...hand,
            extra: {
                ...extra,
                marriages: [...extra.marriages, { seat, suit: d.suit, points, trick: trick.index }],
                shown: [...extra.shown, ...pair.map((card) => ({ seat, card }))],
            },
        },
    };
}
/** Rules §3: winner draws first, then the loser; the loser of the trick that
 *  empties the talon takes the turned card. Nothing is drawn after a close. */
function drawAfterTrick(hand, winner) {
    const { holdings, stock, kitty } = hand.secret;
    const extra = hand.extra;
    if (extra.closed !== null || stock.length + kitty.length === 0)
        return { holdings, stock, kitty, extra };
    const loser = sixtySixOpponent(winner);
    const next = holdings.map((h) => [...h]);
    const pile = [...stock];
    let left = [...kitty];
    let shown = extra.shown;
    next[winner].push(pile.shift());
    if (pile.length > 0)
        next[loser].push(pile.shift());
    else if (left.length > 0) {
        const card = left[0];
        next[loser].push(card);
        left = [];
        shown = [...shown, { seat: loser, card }];
    }
    const exhausted = pile.length + left.length === 0;
    return {
        holdings: next,
        stock: pile,
        kitty: left,
        extra: { ...extra, shown, strictFrom: exhausted ? hand.tricks.length : extra.strictFrom },
    };
}
export const sixtySix = {
    meta: { id: 'sixty-six', name: 'Sixty-six', rotation: 'anticlockwise' },
    defaultConfig: DEFAULT_SIXTY_SIX_CONFIG,
    validateConfig: validateSixtySixConfig,
    table: () => SIXTY_SIX_TABLE,
    tricksPerHand: () => SIXTY_SIX_MAX_TRICKS,
    deck: sixtySixDeck,
    dealPlan: sixtySixDealPlan,
    auction: sixtySixAuction,
    ranking: (hand) => sixtySixRanking(hand.extra.trump ?? hand.contract?.trump ?? null),
    legalPlays: (hand, seat) => (hand.extra.trump ? sixtySixLegalPlays(hand, seat) : []),
    initHandExtra: () => INIT_EXTRA,
    initMatchExtra: () => null,
    onContract(hand, table) {
        return {
            extra: { ...INIT_EXTRA, trump: hand.contract.trump },
            pending: null,
            activeSeats: [0, 1],
            // The non-dealer leads to the first trick.
            leader: (hand.dealer + 1) % table.seatCount,
        };
    },
    applyDecision: (hand, seat, decision) => applySixtySixDecision(hand, seat, decision),
    turnDecisions: (hand, seat) => sixtySixTurnDecisions(hand, seat),
    turnDecisionKey: sixtySixDecisionKey,
    onCardPlayed: (hand) => hand.extra,
    /** Only a trick played under the strict rules shows a void. */
    provesVoid: (_ctx, _led, _card, _config, at) => {
        const from = at?.extra.strictFrom;
        return from !== null && from !== undefined && at !== undefined && at.trick.index >= from;
    },
    handOver: (hand) => {
        const total = sixtySixCountOf(hand).total;
        return total[0] >= SIXTY_SIX_OUT || total[1] >= SIXTY_SIX_OUT;
    },
    afterTrick: (hand, winner) => drawAfterTrick(hand, winner),
    nextDealer: (state) => sixtySixNextDealer(state),
    publicHoldings(hand) {
        const out = [[], []];
        for (const s of sixtySixShown(hand.extra, hand.tricks))
            out[s.seat].push(s.card);
        return out;
    },
    nextLeader: (_hand, winner) => winner,
    scoreHand: (hand, table) => sixtySixScoreHand(hand, table),
    onPassedOut: () => ({ kind: 'redeal' }),
    matchEnd: (state, config) => sixtySixMatchEnd(state, config),
    roles() {
        return { activeSeats: [0, 1], controllerOf: [0, 1] };
    },
    visibility: sixtySixVisibility,
    contractLabel: (contract, _config, extra) => sixtySixContractLabel(contract, extra),
};
//# sourceMappingURL=module.js.map